import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import EventForm from "@/components/admin/EventForm";
import { eventApi } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

const TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Pending Review" },
  { key: "live", label: "Live" },
];

export default function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => eventApi.adminList().then(setEvents);
  useEffect(() => { load(); }, []);

  const onSaved = () => { setShowForm(false); setEditing(null); load(); };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await eventApi.remove(e.id);
    toast.success("Event deleted");
    setEvents((p) => p.filter((x) => x.id !== e.id));
  };

  const publish = async (e) => {
    const upd = await eventApi.update(e.id, { ...e, status: "live" });
    setEvents((p) => p.map((x) => (x.id === e.id ? upd : x)));
    toast.success(`"${e.title}" is now live`);
  };

  const filtered = useMemo(() => {
    if (tab === "all") return events;
    return events.filter((e) => (e.status || "live") === tab);
  }, [events, tab]);

  const pendingCount = events.filter((e) => e.status === "draft").length;

  if (showForm || editing) {
    return (
      <EventForm
        event={editing}
        onCancel={() => { setShowForm(false); setEditing(null); }}
        onSaved={onSaved}
      />
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-black">Events</h1>
          <p className="text-gray-500 mt-1">
            {events.length} total · {pendingCount > 0 ? (
              <span className="text-[#FA003F] font-bold">{pendingCount} awaiting review</span>
            ) : "all reviewed"}
          </p>
        </div>
        <Button
          data-testid="new-event-btn"
          onClick={() => setShowForm(true)}
          className="h-11 rounded-full bg-[#FA003F] hover:bg-[#C40031] font-bold px-5"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Event
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {TABS.map((t) => {
          const count = t.key === "all" ? events.length : events.filter((e) => (e.status || "live") === t.key).length;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`events-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-2 border transition-colors ${
                isActive ? "bg-[#1A1A1A] text-white border-transparent" : "bg-white text-[#1A1A1A] border-black/10 hover:border-[#FA003F]/40"
              }`}
            >
              {t.label}
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20 text-white" : "bg-black/5 text-[#1A1A1A]/70"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-black/10 overflow-hidden">
        <table className="w-full text-sm" data-testid="events-admin-table">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-gray-500 border-b border-black/10">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Event</th>
              <th className="text-left px-6 py-3 font-bold">Date</th>
              <th className="text-left px-6 py-3 font-bold">City</th>
              <th className="text-left px-6 py-3 font-bold">Status</th>
              <th className="text-left px-6 py-3 font-bold">Booking</th>
              <th className="text-right px-6 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((e) => {
              const status = e.status || "live";
              return (
                <tr key={e.id} data-testid={`admin-event-row-${e.id}`} className="hover:bg-[#F9F8F6]">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img src={e.image_url} alt="" className="h-10 w-14 rounded-md object-cover bg-gray-100" />
                      <div>
                        <div className="font-bold line-clamp-1">{e.title}</div>
                        <div className="text-xs text-gray-500">
                          {e.venue}
                          {e.submitter_email && (
                            <> · <span className="text-[#FA003F]">by {e.submitter_email}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(e.event_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap capitalize">{e.city_key}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${
                        status === "live"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {status === "live" ? "Live" : "Pending"}
                    </span>
                  </td>
                  <td className="px-6 py-4"><span className="text-xs font-bold px-2 py-1 rounded-full bg-[#FA003F]/10 text-[#FA003F] capitalize">{e.booking_type}</span></td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      {status === "draft" && (
                        <button
                          data-testid={`publish-event-${e.id}`}
                          onClick={() => publish(e)}
                          title="Publish"
                          className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="h-4 w-4" /> Publish
                        </button>
                      )}
                      <a href={`/event/${e.id}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-black/10 grid place-items-center hover:bg-black hover:text-white transition-colors">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        data-testid={`edit-event-${e.id}`}
                        onClick={() => setEditing(e)}
                        className="h-9 w-9 rounded-full border border-black/10 grid place-items-center hover:bg-black hover:text-white transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        data-testid={`delete-event-${e.id}`}
                        onClick={() => remove(e)}
                        className="h-9 w-9 rounded-full border border-red-200 text-red-600 grid place-items-center hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-gray-500">No events in this tab.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
